import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CountrySelect } from "@/Common/Components/Contacts/CountrySelect";
import { FormFieldRow, IFormFieldRowProps } from "@/Common/Components/Forms/FormFieldRow";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { WorkSafeBcAddressModel } from "./Models/WorkSafeBcAddressModel";
import { WorkSafeBcPhysicianReportModel } from "./Models/WorkSafeBcPhysicianReportModel";

interface IBaseWorkSafeBcSubSectionProps {
    isEditMode: boolean;
    model: WorkSafeBcPhysicianReportModel;
    formId: string;
    mutateModelState: (mutation: (state: WorkSafeBcPhysicianReportModel) => void) => void;
}

class BaseWorkSafeBcSubSection<TProps extends IBaseWorkSafeBcSubSectionProps, TState> extends BaseReactComponent<TProps, TState> {
    protected readonly dateFormatForDateSelector = "yyyy-MM-dd";
    protected readonly dateFormat = "YYYY-MM-DD";
    protected readonly validationHelper = new ValidationHelper();
    protected readonly canadaLookUpId = cacheStore.GetCountryLookups().firstOrDefault(p => p.LookupCode === "CNCANADA")?.LookupID;

    public componentDidMount(): void {
        this.setAcceptableLocales([Locale.en]);
    }

    protected renderFields(options: IFormFieldRowProps, children?: React.ReactNode): JSX.Element {
        return (
            <FormFieldRow
                {...options}
                isEditMode={options.isEditMode ?? this.props.isEditMode}
                locale={Locale.en}
            >
                {children}
            </FormFieldRow>
        );
    }

    protected renderAddress(
        labelKey: string,
        id: string,
        currentAddress: WorkSafeBcAddressModel,
        mutate?: (address: WorkSafeBcAddressModel) => void,
        isClickable: boolean = false
    ): JSX.Element {
        const isEditMode = !!mutate && this.props.isEditMode;
        const onReadOnlyClick = isClickable ?
            () => sessionStore.dispatch(LocalPatientOperations.Profile.AddressLabelClick, { contactPointId: currentAddress.ContactPointId })
            : null;

        const validationRule = this.validationHelper.getPostalCodeValidationRule(currentAddress.CountryLookupId, null);
        const mask = validationRule?.EditMaskPattern ?? "";
        const validationErrorMessage = validationRule?.ValidationErrorMessageLookupId
            ? cacheStore.GetLocalizedValueByLookupId(validationRule.ValidationErrorMessageLookupId) : "";

        return (
            <div>
                <label className="form-label bold">{this.getString(labelKey)}</label>
                {this.renderFields(
                    {
                        labelValueKey: "StreetAddressLabel",
                        value: currentAddress.StreetAddress,
                        isEditMode: isEditMode,
                        onReadOnlyClick: onReadOnlyClick,
                        required: isClickable
                    },
                    <TextboxControl
                        onChange={value => mutate?.(currentAddress.updateAddressModel({ StreetAddress: value }))}
                        value={currentAddress.StreetAddress}
                        maxLength={100}
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "CityLabel",
                        value: currentAddress.City,
                        isEditMode: isEditMode,
                        onReadOnlyClick: onReadOnlyClick,
                        required: true
                    },
                    <TextboxControl
                        onChange={value => mutate?.(currentAddress.updateAddressModel({ City: value }))}
                        value={currentAddress.City}
                        maxLength={100}
                        valid={!!currentAddress.City}
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "StateProvinceLabel",
                        value: cacheStore.GetLocalizedValueByLookupId(currentAddress.StateLookupId),
                        isEditMode: isEditMode,
                        onReadOnlyClick: onReadOnlyClick
                    },
                    <SelectLookupControl
                        IncludeEmptyChoice
                        Value={currentAddress.StateLookupId}
                        AvailableValues={this.getProvinces(currentAddress.CountryLookupId)}
                        Callback={lookUpId => mutate?.(currentAddress.updateAddressModel({ StateLookupId: lookUpId }))}
                    />
                )}
                {this.renderFields(
                    {
                        labelValueKey: "CountryLabel",
                        value: cacheStore.GetLocalizedValueByLookupId(currentAddress.CountryLookupId),
                        isEditMode: isEditMode,
                        onReadOnlyClick: onReadOnlyClick
                    },
                    <>
                        <CountrySelect
                            onCountryChange={lookUpId => mutate?.(currentAddress.updateAddressModel({
                                CountryLookupId: lookUpId,
                                StateLookupId: null,
                                ZipCode: ""
                            }))}
                            initialCountryLookupId={currentAddress.CountryLookupId}
                        />
                    </>
                )}
                {this.renderFields(
                    {
                        labelValueKey: "ZipPostalCodeLabel",
                        value: this.validationHelper.getFormattedPostalCodeString(currentAddress.ZipCode, currentAddress.CountryLookupId),
                        isEditMode: isEditMode,
                        onReadOnlyClick: onReadOnlyClick,
                        required: isClickable
                    },
                    <TextboxMaskedControl
                        isValid={!currentAddress.ZipCode || this.validationHelper.isFieldValid(currentAddress.ZipCode, validationRule)}
                        allowEmpty={true}
                        id={`ZipCode_${id}`}
                        value={currentAddress.ZipCode}
                        style={{ width: "150px" }}
                        onChangeFunction={value => mutate?.(currentAddress.updateAddressModel({ ZipCode: value }))}
                        mask={mask}
                        validationErrorMessage={validationErrorMessage}
                    />
                )}
            </div>
        );
    }

    private getProvinces(selectedCountryLookUpId: string): ComboBoxDropDownItem[] {
        const countryLookUp = cacheStore.GetLookupById(selectedCountryLookUpId);
        return cacheStore.GetCountryRegionLookups(countryLookUp).map(p => p.ToComboboxItem());
    }
}

export { BaseWorkSafeBcSubSection, IBaseWorkSafeBcSubSectionProps };
